import { KeyboardEvent, useEffect, useState } from 'react';
import { ActionIcon, Group, Stack, Text, TextInput } from '@mantine/core';
import { Patient } from '@medplum/fhirtypes';
import { IconCheck, IconX } from '@tabler/icons-react';
import { alert } from '@/services/alert';
import { readLegalName, readPreferredName } from '@/services/patient-name';
import { usePatientProfileEditor } from '@/components/Timeline/TimelineItems/PatientAccessCall/usePatientProfileEditor';

interface PatientNameEditorProps {
  patient: Patient;
  onClose: () => void;
}

/**
 * The header's Edit name (BAR-2993): replaces the name line with legal first,
 * preferred and legal last, in the order the name reads. Labels rather than
 * placeholders, because legal versus preferred is the point.
 *
 * The legal and preferred names are separate saves through the PN card's
 * profile editor, each only when it changed. An emptied preferred name removes it.
 */
export function PatientNameEditor({ patient, onClose }: PatientNameEditorProps) {
  // The card's save path: version-checked, with a Provenance; undefined for a role that cannot update a Patient.
  const saveProfileField = usePatientProfileEditor(patient, false);
  const [initial] = useState(() => ({ ...readLegalName(patient), preferred: readPreferredName(patient) }));
  const [given, setGiven] = useState(initial.given);
  const [preferred, setPreferred] = useState(initial.preferred);
  const [family, setFamily] = useState(initial.family);
  const [error, setError] = useState<string>();
  const [isSaving, setIsSaving] = useState(false);
  // The preferred name waiting for its save, after a legal-name save in the same submit.
  const [pendingPreferred, setPendingPreferred] = useState<{ value: string; afterLegal: boolean }>();

  function handleCancel() {
    if (!isSaving) onClose();
  }

  async function handleSave() {
    if (!saveProfileField || isSaving) return;

    const nextGiven = given.trim();
    const nextFamily = family.trim();
    const nextPreferred = preferred.trim();
    if (!nextGiven && !nextFamily) {
      setError('Add a legal first or last name.');
      return;
    }
    setError(undefined);

    const legalChanged = nextGiven !== initial.given || nextFamily !== initial.family;
    const preferredChanged = nextPreferred !== initial.preferred;
    if (!legalChanged && !preferredChanged) {
      onClose();
      return;
    }

    setIsSaving(true);
    if (legalChanged) {
      try {
        await saveProfileField({ field: 'legalName', value: { given: nextGiven, family: nextFamily } });
      } catch {
        alert.error('Failed to update patient name. Please try again.');
        setIsSaving(false);
        return;
      }
    }
    if (preferredChanged) {
      // Saved by the effect below, from the render that carries the Patient this save wrote.
      setPendingPreferred({ value: nextPreferred, afterLegal: legalChanged });
      return;
    }
    onClose();
  }

  // The handler a submit started with holds the Patient as it was before the
  // legal-name save, and the preferred-name save refuses a patient with no legal
  // name to sit behind. Saving from the next render checks the Patient just written.
  useEffect(() => {
    if (!pendingPreferred || !saveProfileField) return;
    setPendingPreferred(undefined);
    const { value, afterLegal } = pendingPreferred;
    (async () => {
      try {
        await saveProfileField(value ? { field: 'preferredName', value } : { field: 'removePreferredName' });
        onClose();
      } catch {
        alert.error(
          afterLegal
            ? 'Saved the legal name, but not the preferred name. Please try again.'
            : 'Failed to update patient name. Please try again.'
        );
      } finally {
        setIsSaving(false);
      }
    })();
  }, [pendingPreferred, saveProfileField, onClose]);

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      event.preventDefault();
      void handleSave();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      handleCancel();
    }
  }

  if (!saveProfileField) {
    return null;
  }

  const inputProps = { size: 'xs', onKeyDown: handleKeyDown, disabled: isSaving } as const;

  return (
    <Stack gap={4}>
      <Group gap="xs" align="flex-end" wrap="wrap">
        <TextInput
          {...inputProps}
          label="Legal first name"
          value={given}
          onChange={(event) => setGiven(event.currentTarget.value)}
          autoFocus
          w={150}
        />
        <TextInput
          {...inputProps}
          label="Preferred name (optional)"
          value={preferred}
          onChange={(event) => setPreferred(event.currentTarget.value)}
          w={130}
        />
        <TextInput
          {...inputProps}
          label="Legal last name"
          value={family}
          onChange={(event) => setFamily(event.currentTarget.value)}
          w={150}
        />
        <ActionIcon
          color="green.9"
          size={30}
          onClick={() => void handleSave()}
          loading={isSaving}
          aria-label="Save name"
        >
          <IconCheck size={14} />
        </ActionIcon>
        <ActionIcon
          variant="subtle"
          color="gray"
          size={30}
          onClick={handleCancel}
          disabled={isSaving}
          aria-label="Cancel name edit"
        >
          <IconX size={14} />
        </ActionIcon>
      </Group>
      {error && (
        <Text size="xs" c="red" role="alert">
          {error}
        </Text>
      )}
    </Stack>
  );
}
